import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_PHONE_COUNTRY, NANP_COUNTRIES, PHONE_COUNTRIES, phoneCountry } from '../records/countries';
import { parsePhone, toStoredPhone } from '../records/phone';
import { MASKS } from './formattedInput';

/**
 * The Phone Input's logic (piece 3, decision 0340), with no DOM inside: the
 * value the control holds is the stored E.164 string, and the three parts a
 * person edits — the country, the national digits and the extension — are
 * taken off it and put back on it here. A partly typed number rides as a
 * partial E.164 draft, so the parent never sees a second shape; on blur the
 * draft is settled to the stored form where it places, and the problem is
 * named where it does not.
 */
export interface PhoneInputOptions {
  /** The stored value, E.164 or ''; a legacy value with no plus is read as a national number. */
  value: string;
  onChange: (stored: string) => void;
  /** The country the empty control opens on; the United States unless the record says otherwise. */
  defaultCountry?: string;
}

/** The Other choice: a number typed in full after the plus, for a country the table does not seed. */
export const OTHER_PHONE_COUNTRY = '';

export interface PhoneCountryOption {
  code: string;
  label: string;
  /** What the closed box shows at the Small width, the code and the dial code (decision 1451). */
  short: string;
}

/** The dropdown's rows: every seeded country with its dial code, then Other. */
export const PHONE_COUNTRY_OPTIONS: readonly PhoneCountryOption[] = [
  ...PHONE_COUNTRIES.map((row) => ({ code: row.code, label: `${row.name} +${row.dialCode}`, short: `${row.code} +${row.dialCode}` })),
  { code: OTHER_PHONE_COUNTRY, label: 'Other (type the number with its country code)', short: 'Other' },
];

/** The draft a keystroke emits: the code and whatever digits stand so far, or nothing for nothing. */
function draftOf(country: string, national: string): string {
  if (!national) return '';
  const row = phoneCountry(country);
  return row ? `+${row.dialCode}${national}` : `+${national}`;
}

export function usePhoneInput({ value, onChange, defaultCountry = DEFAULT_PHONE_COUNTRY }: PhoneInputOptions) {
  /** The country the person chose; the stored value's own code wins over it while one is stored. */
  const [chosen, setChosen] = useState<string | null>(null);
  const preferred = chosen ?? defaultCountry;
  const parsed = useMemo(() => parsePhone(value, null, preferred), [value, preferred]);
  const country = value.trim() ? parsed.country : preferred;
  const national = parsed.national;
  /** The North American shape takes the Formatted Input mask; every other country types plain digits. */
  const mask = NANP_COUNTRIES.includes(country) ? MASKS.phone : null;
  const problem = useMemo(() => (national ? toStoredPhone(country, national).problem : null), [country, national]);

  const setCountry = useCallback(
    (code: string) => {
      setChosen(code);
      if (national) onChange(draftOf(code, national));
    },
    [national, onChange],
  );
  const typeNational = useCallback((digits: string) => onChange(draftOf(country, digits.replace(/\D/g, ''))), [country, onChange]);
  /** Blur settles the draft: a placeable number is written in its stored form, so a leading one typed on a North American number is dropped. */
  const settle = useCallback(() => {
    if (!national) return;
    const result = toStoredPhone(country, national);
    if (result.stored && result.stored !== value) onChange(result.stored);
  }, [country, national, onChange, value]);
  /** What the field's own validator answers for the digits it holds. */
  const validate = useCallback((digits: string) => (digits ? toStoredPhone(country, digits).problem : null), [country]);

  return { country, national, mask, problem, setCountry, typeNational, settle, validate, options: PHONE_COUNTRY_OPTIONS };
}
